import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EventTypeDropdown from "./EventTypeDropdown";
import IntegrationService from "../../services/integrations/Integrations";
import {generateTimeOptions, convert12To24} from "../../utils/utils";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { useQueryClient } from "@tanstack/react-query";

const timeOptions = generateTimeOptions();
const dayAbbreviations = ["S", "M", "T", "W", "T", "F", "S"];
const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const dayNameToIndex = Object.fromEntries(dayNames.map((name, i) => [name, i]));

const ALLOWED_TIMEZONES = [
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "America/Denver",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Shanghai",
];
const TIMEZONE_OPTIONS = ALLOWED_TIMEZONES;

interface SettingsProps {
  onClose?: () => void;
}
interface TimeSlot {
  start: string;
  end: string;
  timeZone?: string;
}

interface DayConfig {
  day: string;
  available: boolean;
  slots: TimeSlot[];
}

const getDefaultSchedule = (data: any[], selectedZone: string): DayConfig[] => {
  const mapped: DayConfig[] = dayAbbreviations.map((day) => ({
    day,
    available: false,
    slots: [],
  }));

  data.forEach((slot: any) => {
    const dayIndex = dayNameToIndex[slot.dayOfWeek];
    if (dayIndex === undefined) return;
    mapped[dayIndex].available = true;
    const zoneForSlot = slot.timeZone || selectedZone || "UTC";
    mapped[dayIndex].slots.push({
      start: slot.startTime.slice(0, 5),
      end: slot.endTime.slice(0, 5),
      timeZone: zoneForSlot,
    });
  });
  return mapped;
};

const Settings = ({ onClose }: SettingsProps) => {
  const [schedule, setSchedule] = useState<DayConfig[] | null>(null);
  const {showSnackbar} = useDisplaySnackbar();
  const queryClient = useQueryClient();
  const browserTZ = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || "UTC";
  const [timeZone, setTimeZone] = useState<string>(browserTZ);
  const [tzError, setTzError] = useState<string | null>(null);

  const { data: availabilityData, isLoading } = IntegrationService.useGetCalendarAvailability(true);
  const mapped = availabilityData?.data ? getDefaultSchedule(availabilityData.data, timeZone) : getDefaultSchedule([], timeZone);

  const updateAvailabilityMutation = IntegrationService.useUpdateCalendarAvailability({
    onSuccess: () => {
      console.log("Availability updated!");
    },
  });

  useEffect(() => {
    if (!availabilityData?.data) return;
    setSchedule(mapped);
    const tzs = (availabilityData.data || []).map((rec: any) => rec.timeZone).filter(Boolean);
    if (tzs.length > 0) {
      const counts: Record<string, number> = {};
      tzs.forEach((t: string) => (counts[t] = (counts[t] || 0) + 1));
      const mode = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      setTimeZone(mode);
      return;
    }
  }, [availabilityData, isLoading]);

  const updateSlot = (dayIndex: number, slotIndex: number, field: "start" | "end", value: string) => {
    setSchedule((prev) =>
      prev ?
      prev.map((day, i) =>
        i === dayIndex
          ? {
              ...day,
              slots: day.slots.map((slot, si) =>
                si === slotIndex ? { ...slot, [field]: value } : slot
              ),
            }
          : day
      )
      :prev
    );
  };

  const addSlot = (dayIndex: number) => {
    setSchedule((prev) =>
      prev ?
      prev.map((day, i) =>
        i === dayIndex
          ? {
              ...day,
              available: true,
              slots: [...day.slots, { start: "09:00", end: "20:00", timeZone: timeZone }],
            }
          : day
      )
      : prev
    );
  };

  const removeSlot = (dayIndex: number, slotIndex: number) => {
    setSchedule((prev) =>
      prev ?
      prev.map((day, i) =>
        i === dayIndex
          ? {
              ...day,
              slots: day.slots.filter((_, si) => si !== slotIndex),
              available: day.slots.length > 1,
            }
          : day
      )
      : prev
    );
  };

  const handleSave = () => {
    if (!schedule) return;
    const updates: any[] = [];
    const deletes: any[] = [];

    schedule.forEach((dayConfig, idx) => {
      const dayName = dayNames[idx];
      if (dayConfig.available) {
        dayConfig.slots.forEach((slot) => {
          updates.push({
            dayOfWeek: dayName,
            startTime: `${convert12To24(slot.start)}`,
            endTime: `${convert12To24(slot.end)}`,
          });
        });
      } else {
        deletes.push({
          dayOfWeek: dayName,
          startTime: "00:00:00",
          endTime: "00:00:00",
        });
      }
    });

    updateAvailabilityMutation.mutate({ updates, deletes, timeZone },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:["get_calendar_availability"]
        });
        showSnackbar("success", "Availability updated successfully");
      },
      onError: () => {
        showSnackbar("error", "Failed to update availability");
      }
    }
    );
  };

  const handleCancel = () => {
    if(availabilityData?.data){
      setSchedule(mapped);
    }
  };

  const renderTimeSelector = (
    value: string,
    onChange: (val: string) => void
  ) => (
    <FormControl size="small" sx={{ width: 100 }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
      >
        {timeOptions.map((time) => (
          <MenuItem key={time} value={time}>
            {time}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const slotTimeZones = (availabilityData?.data || []).map((s: any) => s.timeZone).filter((tz: string | null) => !!tz);
  const selectableTimezones = Array.from(
    new Set([ timeZone, browserTZ, ...TIMEZONE_OPTIONS, ...slotTimeZones])
  ).filter(Boolean);

  return (
    <Box sx={{ position: "relative", width: "fit-content" }}>
    <Typography
      variant="h5"
      fontWeight={600}
      color="text.secondary"
      sx={{ mb: 1 }}
    >
      Schedule
    </Typography>
    <IconButton
      size="small"
      sx={{ position: "absolute", top: -5, right: 5 }}
      onClick={onClose}
    >
      <CloseIcon />
    </IconButton>
    <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, m: 1, width: "380px" }}>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb:1 }}>
        <Typography variant="body1" fontWeight={500}>
          Active on:
        </Typography>
        <Box sx={{ cursor: "pointer", color: "linear-gradient(180deg, #805EDE 0%, #453378 100%)", fontWeight: 500 }}>
          <EventTypeDropdown />
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body1" fontWeight={500}>
          Time zone :
        </Typography>
        <Autocomplete
          size="small"
          options={selectableTimezones}
          value={timeZone || ""}
          onChange={(_event, newValue: string | null) => {
            const normalized = newValue || "";
            setTimeZone(normalized);

            if (!normalized) {
              setTzError("Time zone is required");
            } else {
              setTzError("");
            }
          }}
          getOptionLabel={(opt) => (typeof opt === "string" ? opt : String(opt))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Time zone"
              placeholder="Search timezone"
              size="small"
              error={!!tzError}
              helperText={tzError || ""} 
            />
          )}
          sx={{ minWidth: 250 }}
          ListboxProps={{ style: { maxHeight: 300 } }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="body1" fontWeight={500} gutterBottom>
        Working hours
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Set when you are typically available for meetings
      </Typography>

      {schedule?.map((dayConfig, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: dayConfig.available ? "linear-gradient(180deg, #805EDE 0%, #453378 100%)" : "#e0e0e0",
              color: dayConfig.available ? "#fff" : "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 500,
            }}
          >
            {dayConfig.day}
          </Box>

          {!dayConfig.available ? (
            <Button size="small" onClick={() => addSlot(index)} startIcon={<AddIcon />}>
              Unavailable
            </Button>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              {dayConfig.slots.map((slot, slotIndex) => (
                <Box
                  key={slotIndex}
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0 }}
                >
                  {renderTimeSelector(slot.start, (val) =>
                    updateSlot(index, slotIndex, "start", val)
                  )}
                  <Typography>-</Typography>
                  {renderTimeSelector(slot.end, (val) =>
                    updateSlot(index, slotIndex, "end", val)
                  )}
                  <IconButton size="small" onClick={() => removeSlot(index, slotIndex)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ background: "linear-gradient(180deg, #805EDE 0%, #453378 100%)", borderRadius: "4px", WebkitTextFillColor: "white", width: "30px", height: "30px" }}
        >
          Save
        </Button>
        <Button variant="contained" onClick={handleCancel} sx={{ borderRadius: "4px", width: "30px", height: "30px", }}>
          Reset
        </Button>
      </Box>
    </Box>
    </Box>
  );
};

export default Settings;